import { useCallback, useEffect, useRef, useState } from 'react'

import { CursorState } from '@editor/config/constants/cursor'
import { usePrintScale } from '@editor/hooks/usePrintScale'
import { PointArray } from '@editor/interfaces/attribute'
import { LineStyle } from '@editor/interfaces/canvas'
import { MeasurementLabelProps } from '@editor/interfaces/canvas/measurementLabel'
import { MAX_TEXT_OFFSET, MIN_TEXT_OFFSET } from '@editor/pages/projects/blueprint-editor/constants'
import { useBlueprintEditorContext } from '@editor/pages/projects/blueprint-editor/contexts/BlueprintEditorContext'
import { BlueprintEditorTools } from '@editor/pages/projects/blueprint-editor/definitions'
import usePointSelection from '@editor/pages/projects/blueprint-editor/hooks/usePointSelection'
import useProjectionPlane from '@editor/pages/projects/blueprint-editor/hooks/useProjectionPlane'
import { hasPreventExitEditingAncestor } from '@editor/pages/projects/blueprint-editor/utils'
import { getPointLineIntersection } from '@editor/services/Points'
import { mmToPointsWithScale, pointsToMmWithScale } from '@editor/services/Util'
import { RootState, useAppDispatch } from '@editor/store/app'
import { resetEditingElementId, resetWorkingElementId, setCursor } from '@editor/store/tool'
import { useFrame, useThree } from '@react-three/fiber'
import { clamp } from 'lodash'
import { useSelector } from 'react-redux'
import { Vector3 } from 'three'
import { useThrottledCallback } from 'use-debounce'

import { getSafeThickness } from '../utils'
import useConfig from './useConfig'
import useObjectsManagement from './useObjectsManagement'
import useUtilities from './useUtilities'

const vector0 = new Vector3(0, 0, 0)

const useAdjustmentHandlers = (props: MeasurementLabelProps, objects: ReturnType<typeof useObjectsManagement>) => {
  const { id, points, offset = 0, visible = true, editable, isWorking, onUpdated } = props
  const {
    lineObject,
    offsetLineObject,

    currentPoints,
    labelPosition,

    setOffsetPositions,
    setCurrentPoints,

    updateLabel,
    updateLine,
  } = objects

  // Store
  const dispatch = useAppDispatch()
  const currentAnchorPosition = useSelector((state: RootState) => state.toolUtil.core.currentAnchorPosition)
  const currentStrokeColor = useSelector((state: RootState) => state.toolUtil.core.currentStrokeColor)
  const selectedElementIds = useSelector((state: RootState) => state.toolUtil.core.selectedElementIds)
  const selectedTool = useSelector((state: RootState) => state.blueprint.editor.selectedTool)

  // Derived
  const isSelected = selectedElementIds.includes(id)
  const isSelectedTool = selectedTool === BlueprintEditorTools.MeasurementLabel

  // Hooks
  const { camera, gl } = useThree()
  const { projectionDirections } = useProjectionPlane(camera, gl)
  const { orthographicCamera, checkPreventExitEditingMode, resetPreventExitEditingMode } = useBlueprintEditorContext()
  const { currentPrintScale } = usePrintScale()
  const { lineConfig } = useConfig(props)
  const { getIntersection } = usePointSelection()
  const { calculateDashGapSizes, calculateOffsetPositions } = useUtilities(props)

  // Refs
  const isWorkingRef = useRef(isWorking)
  const isDraggingLabelRef = useRef(false)
  const isAdjustingAnchorRef = useRef<number | null>(null) // index of the anchor being adjusted
  const isMovingRef = useRef<PointArray | null>(null) // initial mouse position of the move

  const containerRef = useRef<HTMLDivElement | null>(null)
  const labelRef = useRef<HTMLDivElement | null>(null)
  const labelTopLeftRef = useRef<HTMLDivElement | null>(null)
  const labelBottomLeftRef = useRef<HTMLDivElement | null>(null)

  const tempOffset = useRef<number | null>(null)
  const tempPointsRef = useRef<[Vector3, Vector3] | null>(null)
  const tempOffsetPointsRef = useRef<[Vector3, Vector3] | null>(null)
  const dragStartValues = useRef<{
    offset: number
    direction: boolean
  } | null>(null)

  // Declare vectors to avoid re-creating them on every render
  const tempVector1 = useRef(new Vector3())
  const tempVector2 = useRef(new Vector3())

  useEffect(() => {
    isWorkingRef.current = isWorking
  }, [isWorking])

  /**
   * Handle label adjustment when dragging the label
   *
   * @param targetPos The target position in 3D space where the label is being dragged to
   */
  const handleLabelAdjustment = useCallback(
    (targetPos: PointArray) => {
      if (!isDraggingLabelRef.current || !labelRef.current || !lineObject) return

      const p1 = new Vector3().fromArray(currentPoints[0])
      const p2 = new Vector3().fromArray(currentPoints[1])
      const cp = new Vector3().fromArray(targetPos)
      if (p1.equals(p2)) return // avoid zero-length line

      const { distance: rawOffset, side: mouseSide } = getPointLineIntersection(
        p1,
        p2,
        cp,
        projectionDirections?.normal || vector0,
      )
      const newOffset = rawOffset * 1000 // convert to mm

      // We also need to know where does the text label is relative to the line
      const topLeftRect = labelTopLeftRef.current?.getBoundingClientRect()
      const bottomLeftRect = labelBottomLeftRef.current?.getBoundingClientRect()

      if (!topLeftRect || !bottomLeftRect) return

      const textTopPos = getIntersection({ clientX: topLeftRect.left, clientY: topLeftRect.top })
      const textBottomPos = getIntersection({
        clientX: bottomLeftRect.left,
        clientY: bottomLeftRect.top,
      })
      if (!textTopPos || !textBottomPos) return

      const textWorldHeight =
        new Vector3().fromArray(textTopPos).distanceTo(new Vector3().fromArray(textBottomPos)) * 1000
      const { side: topSide } = getPointLineIntersection(
        p1,
        p2,
        new Vector3(...textTopPos),
        projectionDirections?.normal || vector0,
      )
      const { side: bottomSide } = getPointLineIntersection(
        p1,
        p2,
        new Vector3(...textBottomPos),
        projectionDirections?.normal || vector0,
      )

      // if one of the sides is 0, it means it's on the line
      const side = topSide === bottomSide ? topSide : 0

      // convert to print-scale friendly value
      const printScaleOffset = mmToPointsWithScale(newOffset || 0, currentPrintScale)

      // round to integer to avoid excessive decimal places
      const roundedOffset = clamp(Math.round(printScaleOffset * 100) / 100, MIN_TEXT_OFFSET, MAX_TEXT_OFFSET)

      // convert it back to mm.
      // subtract the old offset to take into account mouse will not be at the exact offset position
      const actualOffset =
        pointsToMmWithScale(roundedOffset, currentPrintScale) * mouseSide + (dragStartValues?.current?.offset || 0)
      const newDirection = actualOffset >= 0

      // When the text is on the line itself, clamp the line to 0 offset
      if ((mouseSide * actualOffset < 0 || mouseSide !== side) && dragStartValues.current) {
        tempOffset.current = 0
      } else {
        tempOffset.current = actualOffset

        if (!dragStartValues.current) {
          dragStartValues.current = {
            offset: offset - tempOffset.current,
            direction: offset >= 0,
          }
          tempOffset.current += dragStartValues.current.offset
        }

        // After the label has crossed over to the other side of the line, the offset calculation needs to be adjusted
        if (dragStartValues.current && dragStartValues.current.direction !== newDirection) {
          tempOffset.current -= textWorldHeight * side
        }
      }

      tempOffsetPointsRef.current = calculateOffsetPositions(currentPoints[0], currentPoints[1], tempOffset.current)

      // -- Update geometries
      updateLine([p1, p2], tempOffsetPointsRef.current, tempOffset.current)

      // -- Update label
      // Calculate against actual offset according to mouse position, not where the line is.
      const labelOffsetPositions = calculateOffsetPositions(currentPoints[0], currentPoints[1], actualOffset)
      updateLabel(labelOffsetPositions, dragStartValues.current.direction)
    },
    [
      lineObject,
      isDraggingLabelRef,
      currentPoints,
      projectionDirections,
      offset,
      currentPrintScale,
      calculateOffsetPositions,
      getIntersection,
      updateLine,
      updateLabel,
    ],
  )

  /**
   * Handle label adjustment when creating a new label.
   *
   * @param curPos The current position of the mouse in 3D space.
   */
  const handleWorkingAdjustment = useCallback(
    (curPos: PointArray) => {
      if (!isWorkingRef.current) return

      // -- Update geometries
      // first position is always the start point, second is current anchor position
      tempPointsRef.current = [tempVector1.current.fromArray(points[0]), tempVector2.current.fromArray(curPos)]
      tempOffsetPointsRef.current = calculateOffsetPositions(
        tempPointsRef.current[0].toArray(),
        tempPointsRef.current[1].toArray(),
        offset,
      )
      updateLine(tempPointsRef.current, tempOffsetPointsRef.current, offset)

      // -- Update label
      updateLabel(tempOffsetPointsRef.current, offset >= 0)
      setDistance(tempOffsetPointsRef.current[0].distanceTo(tempOffsetPointsRef.current[1]))
    },
    [isWorkingRef, points, offset, updateLine, updateLabel, calculateOffsetPositions],
  )

  /**
   * Handle label adjustment when changing the offset value.
   * Use points instead of currentPoints here since updateLine updates currentPoints.
   * Using currentPoints will cause infinite loop.
   *
   * @param newOffset The new offset value in mm.
   */
  const handleOffsetAdjustment = useCallback(
    (newOffset: number) => {
      // -- Update geometries
      // first position is always the start point, second is current anchor position
      tempPointsRef.current = [tempVector1.current.fromArray(points[0]), tempVector2.current.fromArray(points[1])]
      tempOffsetPointsRef.current = calculateOffsetPositions(
        tempPointsRef.current[0].toArray(),
        tempPointsRef.current[1].toArray(),
        newOffset,
      )
      updateLine(tempPointsRef.current, tempOffsetPointsRef.current, newOffset)

      // -- Update label
      updateLabel(tempOffsetPointsRef.current, newOffset >= 0)
      setDistance(tempOffsetPointsRef.current[0].distanceTo(tempOffsetPointsRef.current[1]))
    },
    [points, updateLine, updateLabel, calculateOffsetPositions],
  )

  /**
   * Handle adjusting position of any side of the label.
   *
   * @param targetPos The new position of the anchor being adjusted.
   */
  const handleAnchorAdjustment = useCallback(
    (targetPos: PointArray) => {
      if (isAdjustingAnchorRef.current === null) return

      // Figure out which anchor is being adjusted
      tempPointsRef.current = [
        tempVector1.current.fromArray(isAdjustingAnchorRef.current === 0 ? targetPos : points[0]),
        tempVector2.current.fromArray(isAdjustingAnchorRef.current === 1 ? targetPos : points[1]),
      ]

      // -- Update geometries
      // first position is always the start point, second is current anchor position
      tempOffsetPointsRef.current = calculateOffsetPositions(
        tempPointsRef.current[0].toArray(),
        tempPointsRef.current[1].toArray(),
        offset,
      )
      updateLine(tempPointsRef.current, tempOffsetPointsRef.current, offset)

      // -- Update label
      updateLabel(tempOffsetPointsRef.current, offset >= 0)
      setDistance(tempOffsetPointsRef.current[0].distanceTo(tempOffsetPointsRef.current[1]))
    },
    [isAdjustingAnchorRef, points, offset, updateLine, updateLabel, calculateOffsetPositions],
  )

  /**
   * Handle moving the entire label.
   *
   * @param targetPos The new position based on mouse movement.
   */
  const handleMoveAdjustment = useCallback(
    (targetPos: PointArray) => {
      if (!isMovingRef.current) return

      // Figure out new position based on initial position and current mouse position
      const relativePosition = [
        targetPos[0] - isMovingRef.current[0],
        targetPos[1] - isMovingRef.current[1],
        targetPos[2] - isMovingRef.current[2],
      ]

      tempPointsRef.current = [
        tempVector1.current.set(
          points[0][0] + relativePosition[0],
          points[0][1] + relativePosition[1],
          points[0][2] + relativePosition[2],
        ),
        tempVector2.current.set(
          points[1][0] + relativePosition[0],
          points[1][1] + relativePosition[1],
          points[1][2] + relativePosition[2],
        ),
      ]

      // -- Update geometries
      // first position is always the start point, second is current anchor position
      tempOffsetPointsRef.current = calculateOffsetPositions(
        tempPointsRef.current[0].toArray(),
        tempPointsRef.current[1].toArray(),
        offset,
      )
      updateLine(tempPointsRef.current, tempOffsetPointsRef.current, offset)

      // -- Update label
      updateLabel(tempOffsetPointsRef.current, offset >= 0)
    },
    [points, offset, updateLine, updateLabel, calculateOffsetPositions],
  )

  /**
   * Handle style adjustments such as color, thickness, and line style.
   */
  const handleStylesAdjustment = useCallback(() => {
    if (!lineObject) return

    // -- Update styles
    lineObject.material.color.set(
      (isSelected || isWorkingRef.current) && currentStrokeColor ? currentStrokeColor : lineConfig?.color || '#0bc5ea',
    )
    lineObject.material.linewidth = getSafeThickness(lineConfig?.thickness)
    lineObject.material.dashed = lineConfig?.style === LineStyle.Dashed

    const { dashSize, gapSize } = calculateDashGapSizes()
    offsetLineObject?.forEach((l) => {
      l.material.color.set(
        (isSelected || isWorkingRef.current) && currentStrokeColor
          ? currentStrokeColor
          : lineConfig?.color || '#0bc5ea',
      )
      l.material.linewidth = getSafeThickness(lineConfig?.thickness)

      // Update dash and gap sizes
      if (l.material.dashed) {
        l.material.dashSize = dashSize
        l.material.gapSize = gapSize
      }
    })
  }, [lineConfig, currentStrokeColor, isSelected, lineObject, offsetLineObject, calculateDashGapSizes])

  // States
  const [distance, setDistance] = useState(() => {
    if (!points[0] || !points[1]) return 0
    return tempVector1.current.fromArray(points[0]).distanceTo(tempVector2.current.fromArray(points[1]))
  })

  /**
   * Handle mouse up event and save any adjustments.
   */
  useEffect(() => {
    const handleLabelMouseUp = (ev: MouseEvent) => {
      if (
        !editable ||
        (isAdjustingAnchorRef.current === null &&
          isMovingRef.current === null &&
          !isDraggingLabelRef.current &&
          !isWorkingRef.current)
      )
        return

      // -- Working label adjustments
      // -- Anchor adjustments
      const isSelfPreventExit = hasPreventExitEditingAncestor(ev.target as HTMLElement)
      if (
        (isWorkingRef.current && !checkPreventExitEditingMode() && !isSelfPreventExit) ||
        isAdjustingAnchorRef.current !== null ||
        isMovingRef.current !== null
      ) {
        if (tempPointsRef.current) {
          const newPoints: [PointArray, PointArray] = [
            tempPointsRef.current[0].toArray(),
            tempPointsRef.current[1].toArray(),
          ]
          onUpdated?.({
            id,
            points: newPoints,
          })
          setCurrentPoints(newPoints)
        }

        resetPreventExitEditingMode()
        dispatch(resetWorkingElementId(id))
        isWorkingRef.current = false
        isAdjustingAnchorRef.current = null
        isMovingRef.current = null
      }

      // -- Label adjustments
      if (isDraggingLabelRef.current) {
        if (tempOffset.current !== null) {
          onUpdated?.({ id, offset: tempOffset.current })
          tempOffset.current = null
        }

        if (tempOffsetPointsRef.current) {
          setOffsetPositions(tempOffsetPointsRef.current)
          tempOffsetPointsRef.current = null
        }

        dragStartValues.current = null
        isDraggingLabelRef.current = false
      }

      tempPointsRef.current = null
      tempOffsetPointsRef.current = null
      dispatch(resetEditingElementId(id))

      // -- Update mouse cursor
      dispatch(setCursor(isSelectedTool ? CursorState.CROSSHAIR : CursorState.DEFAULT))
    }

    window.addEventListener('mouseup', handleLabelMouseUp)
    return () => window.removeEventListener('mouseup', handleLabelMouseUp)
  }, [
    id,
    editable,
    isWorkingRef,
    isSelectedTool,
    checkPreventExitEditingMode,
    resetPreventExitEditingMode,
    setCurrentPoints,
    setOffsetPositions,
    dispatch,
    onUpdated,
  ])

  /**
   * Update visual aspect that needs to be adjusted on every frame.'
   * Wrap in useThrottledCallback to avoid excessive calculations.
   */
  const frameHandler = useThrottledCallback(() => {
    if (!visible || !containerRef.current) return

    // --- Update text annotation box (size, scale, etc)
    // Scale based on zoom
    const transforms = [
      `translate(-50%, -50%)`,
      `rotate(${-labelPosition.angle}deg)`,
      `scale(${orthographicCamera.current.zoom})`,
    ]
    containerRef.current?.style.setProperty('transform', transforms.join(' '))

    // -- Style adjustment always runs
    handleStylesAdjustment()

    // Need permission to edit
    if (!editable) return

    // -- Geometry adjustment handlers
    if (!currentAnchorPosition) return

    // geometries update as needed
    if (isDraggingLabelRef.current) handleLabelAdjustment(currentAnchorPosition)
    else if (isAdjustingAnchorRef.current !== null) handleAnchorAdjustment(currentAnchorPosition)
    else if (isMovingRef.current) handleMoveAdjustment(currentAnchorPosition)
    else if (isWorkingRef.current) handleWorkingAdjustment(currentAnchorPosition)
  }, 1000 / 60)
  useFrame(frameHandler)

  /**
   * Update lines and label when offset changes.
   * This is generally a reaction to change of offset outside of this component.
   */
  useEffect(() => handleOffsetAdjustment(offset), [offset, handleOffsetAdjustment])

  return {
    distance,

    isDraggingLabelRef,
    isAdjustingAnchorRef,
    isMovingRef,

    containerRef,
    labelRef,
    labelTopLeftRef,
    labelBottomLeftRef,
  }
}

export default useAdjustmentHandlers
